import { useEffect, useRef } from 'react';

interface UsePollingOptions {
  /** Интервал между вызовами в миллисекундах. */
  intervalMs: number;
  /** Опрос можно временно выключить (например, пока чат не выбран). */
  enabled?: boolean;
  /** Не опрашивать, пока вкладка скрыта, и сразу обновиться при возвращении. */
  pauseWhenHidden?: boolean;
}

/**
 * Периодически вызывает callback, не перезапуская таймер при каждом его изменении.
 *
 * Используется как транспорт обновлений чата: когда появится WebSocket,
 * достаточно будет заменить этот хук в двух местах.
 */
export function usePolling(
  callback: () => void,
  { intervalMs, enabled = true, pauseWhenHidden = true }: UsePollingOptions
): void {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    const isHidden = () =>
      pauseWhenHidden &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden';

    const tick = () => {
      if (!isHidden()) {
        savedCallback.current();
      }
    };

    const intervalId = setInterval(tick, intervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        savedCallback.current();
      }
    };

    if (pauseWhenHidden && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      clearInterval(intervalId);
      if (pauseWhenHidden && typeof document !== 'undefined') {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      }
    };
  }, [enabled, intervalMs, pauseWhenHidden]);
}
